export type GroupBounds = {
  id: string;
  name: string;
  color: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type GroupLabelLayout = GroupBounds & {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type GroupLabelObstacle = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type GroupLabelLineObstacle = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

const LABEL_HEIGHT = 26;
const LABEL_GAP = 7;
const WORLD_MARGIN = 6;

function labelWidth(name: string) {
  return Math.max(62, Math.min(190, Array.from(name).length * 9 + 22));
}

function overlaps(
  a: Pick<GroupLabelLayout, "x" | "y" | "width" | "height">,
  b: Pick<GroupLabelLayout, "x" | "y" | "width" | "height">,
) {
  return a.x < b.x + b.width + LABEL_GAP
    && a.x + a.width + LABEL_GAP > b.x
    && a.y < b.y + b.height + LABEL_GAP
    && a.y + a.height + LABEL_GAP > b.y;
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}

function lineIntersectsLabel(
  line: GroupLabelLineObstacle,
  label: { x: number; y: number; width: number; height: number },
) {
  const left = label.x - LABEL_GAP;
  const top = label.y - LABEL_GAP;
  const right = label.x + label.width + LABEL_GAP;
  const bottom = label.y + label.height + LABEL_GAP;
  let start = 0;
  let end = 1;
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  for (const [direction, distance] of [
    [-dx, line.x1 - left],
    [dx, right - line.x1],
    [-dy, line.y1 - top],
    [dy, bottom - line.y1],
  ]) {
    if (direction === 0) {
      if (distance < 0) return false;
      continue;
    }
    const ratio = distance / direction;
    if (direction < 0) {
      if (ratio > end) return false;
      start = Math.max(start, ratio);
    } else {
      if (ratio < start) return false;
      end = Math.min(end, ratio);
    }
  }
  return true;
}

export function placeGroupLabels(
  bounds: GroupBounds[],
  worldWidth: number,
  worldHeight: number,
  obstacles: GroupLabelObstacle[] = [],
  lineObstacles: GroupLabelLineObstacle[] = [],
  diagramCenter?: { x: number; y: number },
): GroupLabelLayout[] {
  const placed: GroupLabelLayout[] = [];

  for (const group of bounds) {
    const width = labelWidth(group.name);
    const centerX = (group.left + group.right) / 2;
    const centerY = (group.top + group.bottom) / 2;
    const outwardX = centerX - (diagramCenter?.x ?? centerX);
    const outwardY = centerY - (diagramCenter?.y ?? centerY);
    const outwardCandidate = !diagramCenter
      ? [group.left + 30, group.top - LABEL_HEIGHT / 2] as const
      : Math.abs(outwardX) > Math.abs(outwardY)
        ? [outwardX < 0 ? group.left - width / 2 : group.right - width / 2, centerY - LABEL_HEIGHT / 2] as const
        : [centerX - width / 2, outwardY > 0 ? group.bottom - LABEL_HEIGHT / 2 : group.top - LABEL_HEIGHT / 2] as const;
    const rawCandidates = [
      outwardCandidate,
      [group.left + 30, group.top - LABEL_HEIGHT / 2],
      [group.right - width - 30, group.top - LABEL_HEIGHT / 2],
      [centerX - width / 2, group.top - LABEL_HEIGHT / 2],
      [group.left + 30, group.bottom - LABEL_HEIGHT / 2],
      [group.right - width - 30, group.bottom - LABEL_HEIGHT / 2],
      [centerX - width / 2, group.bottom - LABEL_HEIGHT / 2],
      [group.left - width / 2, centerY - LABEL_HEIGHT / 2],
      [group.right - width / 2, centerY - LABEL_HEIGHT / 2],
      [group.left + 12, group.top - LABEL_HEIGHT - LABEL_GAP],
      [group.right - width - 12, group.top - LABEL_HEIGHT - LABEL_GAP],
      [group.left + 12, group.bottom + LABEL_GAP],
      [group.right - width - 12, group.bottom + LABEL_GAP],
    ] as Array<readonly [number, number]>;
    const primary = { x: outwardCandidate[0], y: outwardCandidate[1] };
    const localCandidates = [...rawCandidates];
    for (let offset = 0; offset <= 96; offset += 12) {
      for (let x = group.left - width / 2; x <= group.right - width / 2; x += 12) {
        localCandidates.push(
          [x, group.top - LABEL_HEIGHT / 2 - offset],
          [x, group.bottom - LABEL_HEIGHT / 2 + offset],
        );
      }
      for (let y = group.top - LABEL_HEIGHT / 2; y <= group.bottom - LABEL_HEIGHT / 2; y += 12) {
        localCandidates.push(
          [group.left - width / 2 - offset, y],
          [group.right - width / 2 + offset, y],
        );
      }
    }
    const seen = new Set<string>();
    const candidates = localCandidates.map(([x, y]) => ({
      x: clamp(x, WORLD_MARGIN, worldWidth - width - WORLD_MARGIN),
      y: clamp(y, WORLD_MARGIN, worldHeight - LABEL_HEIGHT - WORLD_MARGIN),
      width,
      height: LABEL_HEIGHT,
    })).filter((candidate) => {
      const key = `${candidate.x}:${candidate.y}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) =>
      (a.x - primary.x) ** 2 + (a.y - primary.y) ** 2
      - ((b.x - primary.x) ** 2 + (b.y - primary.y) ** 2));

    const clearsObstacles = (candidate: { x: number; y: number; width: number; height: number }) =>
      obstacles.every((obstacle) =>
        candidate.x + candidate.width + LABEL_GAP <= obstacle.left
        || obstacle.right + LABEL_GAP <= candidate.x
        || candidate.y + candidate.height + LABEL_GAP <= obstacle.top
        || obstacle.bottom + LABEL_GAP <= candidate.y);
    const clearsLines = (candidate: { x: number; y: number; width: number; height: number }) =>
      lineObstacles.every((line) => !lineIntersectsLabel(line, candidate));
    let position = candidates.find((candidate) =>
      placed.every((label) => !overlaps(candidate, label))
      && clearsObstacles(candidate)
      && clearsLines(candidate));
    if (!position) {
      const fallbacks = [];
      for (let y = WORLD_MARGIN; y <= worldHeight - LABEL_HEIGHT - WORLD_MARGIN; y += 12) {
        for (let x = WORLD_MARGIN; x <= worldWidth - width - WORLD_MARGIN; x += 12) {
          fallbacks.push({ x, y, width, height: LABEL_HEIGHT });
        }
      }
      fallbacks.sort((a, b) =>
        (a.x - primary.x) ** 2 + (a.y - primary.y) ** 2
        - ((b.x - primary.x) ** 2 + (b.y - primary.y) ** 2));
      position = fallbacks.find((candidate) =>
        placed.every((label) => !overlaps(candidate, label))
        && clearsObstacles(candidate)
        && clearsLines(candidate));
    }
    position ??= {
      x: WORLD_MARGIN,
      y: WORLD_MARGIN + placed.length * (LABEL_HEIGHT + LABEL_GAP),
      width,
      height: LABEL_HEIGHT,
    };
    placed.push({ ...group, ...position });
  }

  return placed;
}
